import { getApiUrl } from '../config';
import api from './apiService';

export interface Message {
  id?: string;
  sender: 'user' | 'ai' | 'system';
  message: string;
  timestamp?: Date;
  sequence?: string;
  kind?: 'chat' | 'runtime_followup' | 'secret_request' | 'status';
  metadata?: Record<string, any> | null;
  resolvedAt?: Date | null;
}

export type ChefJobStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';

export interface ChefResponseJob {
  id: string;
  conversationId: string;
  userMessageId: string;
  finalMessageId: string | null;
  status: ChefJobStatus;
  statusText: string | null;
  partialContent: string;
  recipeMutated: boolean;
  errorCode: string | null;
  errorMessage: string | null;
  attempt: number;
  cancelRequested: boolean;
  createdAt: Date;
  updatedAt: Date;
  completedAt: Date | null;
}

export interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  recipeId: number | null;
  scope: 'recipe' | 'account';
  origin: 'web' | 'email';
  userId: string;
  createdAt: Date;
  updatedAt: Date;
  isRead: boolean;
  nextCursor?: string | null;
  metadata?: {
    isFollowupConversation?: boolean;
    isRecipeDraftConversation?: boolean;
    followupType?: 'public' | 'private';
    followupDescription?: string;
    followupGuidance?: string;
    followupSample?: string;
    recipeVersion?: string;
    isSecretRequest?: boolean;
    secretLabel?: string;
    secretService?: string | null;
    secretSource?: 'recipe' | 'missing';
    secretResolved?: boolean;
    followupsAddressed?: Array<{
      description: string;
      userResponse: string;
      timestamp: string;
    }>;
    [key: string]: any;
  };
  chefJob?: ChefResponseJob | null;
}


// Get all conversations, optionally filtered by recipe
export const getConversations = async (recipeId?: string): Promise<Conversation[]> => {
  try {
    // Check if recipeId contains a cache-busting parameter
    let cleanRecipeId = recipeId;
    let cacheBuster = '';

    if (recipeId && recipeId.includes('&_=')) {
      // Extract the cache buster
      const parts = recipeId.split('&_=');
      cleanRecipeId = parts[0];
      cacheBuster = `&_=${parts[1]}`;
    }

    // Check if the ID is actually a number and can be parsed properly
    if (cleanRecipeId) {
      const parsedId = parseInt(cleanRecipeId, 10);

      // If it's not a valid number, log a warning
      if (isNaN(parsedId)) {
        console.warn('[DEBUG] ⚠️ Invalid recipeId format - Not a number:', cleanRecipeId);
      }
    }

    const url = cleanRecipeId
      ? `${getApiUrl('conversations')}?recipeId=${cleanRecipeId}${cacheBuster}`
      : getApiUrl('conversations');

    const response = await api.get<Conversation[]>(url);

    return response.data;
  } catch (error) {
    console.error('Error fetching conversations:', error);
    throw error;
  }
};

// Get a specific conversation by ID
export const getConversation = async (id: string, before?: string): Promise<Conversation> => {
  try {
    const response = await api.get<Conversation>(
      `${getApiUrl(`conversations/${id}`)}${before ? `?before=${encodeURIComponent(before)}` : ''}`
    );

    return response.data;
  } catch (error) {
    console.error(`Error fetching conversation ${id}:`, error);
    throw error;
  }
};

// Create a new conversation
export const createConversation = async (
  title: string,
  recipeId?: string,
  messages?: Message[]
): Promise<Conversation> => {
  try {
    const response = await api.post<Conversation>(
      getApiUrl('conversations'),
      { title, recipeId, messages }
    );

    return response.data;
  } catch (error) {
    console.error('Error creating conversation:', error);
    throw error;
  }
};

// Update an existing conversation
export const updateConversation = async (
  id: string,
  updates: { title?: string; messages?: Message[] }
): Promise<Conversation> => {
  try {
    const response = await api.put<Conversation>(
      getApiUrl(`conversations/${id}`),
      updates
    );

    return response.data;
  } catch (error) {
    console.error(`Error updating conversation ${id}:`, error);
    throw error;
  }
};

// Add a message to a conversation
export const addMessage = async (
  conversationId: string,
  sender: 'user' | 'ai',
  message: string
): Promise<Conversation> => {
  try {
    const response = await api.post<Conversation>(
      getApiUrl(`conversations/${conversationId}/messages`),
      { sender, message }
    );

    return response.data;
  } catch (error) {
    console.error(`Error adding message to conversation ${conversationId}:`, error);
    throw error;
  }
};

// Response interface for delete operation
export interface DeleteResponse {
  message: string;
  deletedAt: string;
  conversationId: string;
}

// Soft delete a conversation
export const deleteConversation = async (id: string): Promise<DeleteResponse> => {
  try {
    const response = await api.delete<DeleteResponse>(
      getApiUrl(`conversations/${id}`)
    );
    return response.data;
  } catch (error) {
    console.error(`Error deleting conversation ${id}:`, error);
    throw error;
  }
};

// Restore a deleted conversation
export const restoreConversation = async (id: string): Promise<Conversation> => {
  try {
    const response = await api.post<{ message: string, conversation: Conversation }>(
      getApiUrl(`conversations/${id}/restore`),
      {}
    );
    return response.data.conversation;
  } catch (error) {
    console.error(`Error restoring conversation ${id}:`, error);
    throw error;
  }
};

// Mark a conversation as read
export const markConversationAsRead = async (id: string): Promise<Conversation> => {
  try {
    const response = await api.post<{ message: string, conversation: Conversation }>(
      getApiUrl(`conversations/${id}/read`),
      {}
    );
    return response.data.conversation;
  } catch (error) {
    console.error(`Error marking conversation ${id} as read:`, error);
    throw error;
  }
};
